import type { MicroAgent } from "@newrelic/browser-agent";
import { InstantConfigService } from "@identity/services";
import { Logger } from "@identity/utils";

interface NewRelicOptions {
  version: string;
  sampleRate: number;
}

export class NewRelicLoader {
  private readonly logger: Logger;
  private readonly actionQueue: [name: string, attributes?: object][] = [];
  private readonly errorQueue: Error[] = [];
  private enabled = false;
  private loaded = false;
  private loadPromise: Promise<void> | undefined;
  private agent: MicroAgent | undefined;

  constructor(private readonly options: NewRelicOptions) {
    this.logger = new Logger(window);
    this.logger.setLoggerGroup("NewRelic");
  }

  public async init(instantConfig: InstantConfigService): Promise<void> {
    this.enabled = instantConfig.get("icNewRelic", false);
    this.logger.info(
      `NewRelic is ${this.enabled ? "enabled" : "disabled"}`,
      this.options,
    );
  }

  public async load(): Promise<void> {
    if (!this.enabled) {
      return;
    }

    if (Math.random() > this.options.sampleRate) {
      return;
    }

    if (this.loadPromise === undefined) {
      this.loadPromise = new Promise((resolve) => {
        this.logger.info("MicroAgent loaded");
        import(/* webpackChunkName: "nr-agent" */ "./new-relic-agent").then(
          ({ MicroAgent }) => {
            this.agent = new MicroAgent({
              info: {
                beacon: "bam.eu01.nr-data.net",
                errorBeacon: "bam.eu01.nr-data.net",
                licenseKey: "NRJS-b979b7ac62a2815b308",
                applicationID: "538605062",
                sa: 1,
              },
              loader_config: {
                accountID: "4475772",
                trustKey: "4475772",
                agentID: "538605062",
                licenseKey: "NRJS-b979b7ac62a2815b308",
                applicationID: "538605062",
              },
              init: {
                ajax: { enabled: false },
                distributed_tracing: { enabled: false },
                jserrors: { enabled: true },
                metrics: { enabled: true },
                page_action: { enabled: true },
                page_view_timing: { enabled: true },
                session_trace: { enabled: false },
                session_replay: { enabled: false },
                privacy: { cookies_enabled: true },
                spa: { enabled: false },
              },
            });
            this.loaded = true;
            this.flushQueue();
            resolve();
          },
        );
      });
    }

    return this.loadPromise;
  }

  captureException(error: Error): void {
    if (!this.enabled) {
      return;
    }

    if (!this.loaded) {
      this.errorQueue.push(error);
      this.load();
      return;
    }

    this.logger.error("Error captured", error);
    this.agent?.noticeError(error);
  }

  addPageAction(name: string, attributes?: object) {
    if (!this.enabled) {
      return;
    }

    if (!this.loaded) {
      this.actionQueue.push([name, attributes]);
      this.load();
      return;
    }

    this.logger.info(`Reporting page action: ${name}`, attributes ?? {});
    this.agent?.addPageAction(name, attributes);
  }

  private flushQueue(): void {
    this.actionQueue.forEach(([name, attributes]) => {
      this.logger.info(`Reporting page action: ${name}`, attributes ?? {});
      this.agent?.addPageAction(name, attributes);
    });
    this.actionQueue.length = 0;
    this.errorQueue.forEach((error) => {
      this.logger.error("Error captured", error);
      this.agent?.noticeError(error);
    });
    this.errorQueue.length = 0;
  }
}
